import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Spin,
} from "antd";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { fetchAllBranches, fetchBranchMembers } from "../../../apis/branch.api";
import { fetchClientDetails } from "../../../apis/client.api";
import {
  cancelPinelabTransaction,
  cancelRazorpayPosTransaction,
  checkAndConfirmOrderPayment,
  createEstimateTransaction,
  createPinelabTransaction,
  createRazorpayPosTransaction,
  fetchPIfromId,
  getEstimateTransactions,
  getEzetapBranchDetails,
  getPinelabTransaction,
  getRazorpayPosTransaction,
  addExpressDeliveryCharge,
  addUnloadingCharge,
} from "../../../apis/pro_forma_invoice.api";
import { editUser } from "../../../apis/user.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import {
  PaymentMediumOptions,
  PaymentModeOptions,
} from "../order_placed_details_drawer";

export interface ChargeDetailsDrawerProps {
  onFinish: (values: any) => void;
  onCancel: () => void;
  estimateId: number;
  chargeType: "express_delivery" | "unloading";
  title: string;
  // Override for the charge write itself. The default posts the PI/estimate-level "add" APIs;
  // the OMS order screen passes its own callback (SET semantics via the OMS escalation
  // endpoints, which also hand back the refreshed order). Return false to keep the form open —
  // the API has already surfaced its refusal. When provided, the PI permission gate is skipped
  // (the caller's menu owns gating) AND the payment details are handed over as a nested
  // `payment` object instead of being recorded here: the OMS endpoints take charge + payment in
  // ONE request (the per-field rules — collector for cash/waive-off, reference for NEFT/UPI/
  // Razorpay/POS-manual, PAN over ₹2L, the waive-off cap — are re-enforced server-side), so the
  // POS device create-and-poll flows are NOT driven in this mode; a POS payment is recorded as
  // collected at the device.
  onAddCharge?: (values: {
    amount?: number;
    reason?: string;
    payment?: Record<string, unknown>;
  }) => Promise<boolean>;
  // The OMS express upgrade works without a charge; the PI flow always requires one. Default true.
  chargeRequired?: boolean;
  // Adds the escalation-style optional reason field (kept on the OMS escalation ledger).
  showReason?: boolean;
  // Intro copy above the form (the escalation modal's confirm text).
  description?: ReactNode;
}

export const PaymentTypes = (pendingAmount: number) => [
  { value: "cash", label: "Cash", referenceTypes: ["others"] },
  {
    value: "neft_rtgs_imps",
    label: "NEFT / RTGS / IMPS",
    referenceTypes: ["utr"],
  },
  { value: "upi", label: "UPI", referenceTypes: ["upi_transaction_id"] },
  { value: "razorpay", label: "Razorpay", referenceTypes: ["others"] },
  { value: "pos-manual", label: "POS Manual", referenceTypes: ["others"] },
  {
    value: "waive-off",
    label: "Waive off this additional charge",
    referenceTypes: ["others"],
    disabled: pendingAmount > 1000,
  },
];

export const PaymentReferenceTypes = [
  { value: "utr", label: "UTR" },
  { value: "upi_transaction_id", label: "UPI Transaction ID" },
  { value: "others", label: "Others" },
];

const POSPaymentModes = [
  {
    value: "upi",
    label: "UPI",
  },
  {
    value: "card",
    label: "Card",
  },
  {
    value: "bank_emi",
    label: "Bank EMI",
  },
  {
    value: "brand_emi",
    label: "Brand EMI",
  },
];

const RazorpayPOSPaymentModes = [
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bharatqr", label: "BharatQR" },
  { value: "all", label: "All Modes" },
];

export function ChargeDetailsDrawer({
  onFinish,
  onCancel,
  estimateId,
  chargeType,
  title,
  onAddCharge,
  chargeRequired = true,
  showReason,
  description,
}: ChargeDetailsDrawerProps) {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("expressunloadingtag.addel");
  const [paymentType, setPaymentType] = useState<string | undefined>(undefined);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [estimateDetails, setEstimateDetails] = useState<any>(null);
  const [posPaymentMode, setPosPaymentMode] = useState<string>();
  const [branchMembers, setBranchMembers] = useState<any[]>([]);
  const [allbranchMembers, setAllBranchMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pineLabTransactionId, setPineLabTransactionId] = useState<
    number | null
  >(null);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [branchList, setBranchList] = useState<any[]>([]);
  const [currentAmount, setCurrentAmount] = useState<number>(0);
  const [chargeAmount, setChargeAmount] = useState<number>(0);
  const [ezetapDevices, setEzetapDevices] = useState<any>(null);
  const [razorpayPosTransactionId, setRazorpayPosTransactionId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const clientId = estimateDetails?.client?.id ?? estimateDetails?.client;
      if (!clientId) return;
      if (currentAmount > 2_00_000 && paymentType === "cash") {
        const clientDetails = await fetchClientDetails(clientId);
        setTimeout(() => {
          form.setFieldsValue({
            pan_number: clientDetails?.pan,
          });
        }, 700);
      }
    })();
  }, [currentAmount, estimateDetails, form]);

  const onPosCancel = async () => {
    if (razorpayPosTransactionId) {
      const txn = await cancelRazorpayPosTransaction(razorpayPosTransactionId);
      if (txn) {
        setRazorpayPosTransactionId(null);
        message.success("Transaction cancelled successfully");
      } else {
        message.error("Failed to cancel transaction");
      }
    } else if (pineLabTransactionId) {
      const txn = await cancelPinelabTransaction(pineLabTransactionId);
      if (txn) {
        setPineLabTransactionId(null);
        message.success("Transaction cancelled successfully");
      } else {
        message.error("Failed to cancel transaction");
      }
    }

    setLoading(false);
    form.resetFields();
    setPaymentType(undefined);
    setPosPaymentMode(undefined);
    onCancel();
    clearInterval(intervalId.current as NodeJS.Timeout);
  };

  useEffect(() => {
    fetchBranchMembers().then((res) => {
      setAllBranchMembers(res)
    })
    const activeTransactionId = pineLabTransactionId || razorpayPosTransactionId;
    const isRazorpayPos = !!razorpayPosTransactionId;
    if (!activeTransactionId) return;
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
    intervalId.current = setInterval(async () => {
      const txn = isRazorpayPos
        ? await getRazorpayPosTransaction(activeTransactionId)
        : await getPinelabTransaction(activeTransactionId);
      if (txn?.status === "captured") {
        setLoading(false);
        message.success("Payment successful");
        if (isRazorpayPos) {
          setRazorpayPosTransactionId(null);
        } else {
          setPineLabTransactionId(null);
        }
        form.resetFields();

        const newTxns = await getEstimateTransactions(estimateId);

        setTransactions(newTxns);
        onFinish({ payment_type: isRazorpayPos ? "razorpay-pos" : "pos" });
      } else if (txn?.status === "pending") {
        // Do nothing
      } else if (txn?.status === "failed") {
        setLoading(false);
        message.error("Payment failed");
        if (isRazorpayPos) {
          setRazorpayPosTransactionId(null);
        } else {
          setPineLabTransactionId(null);
        }
        form.resetFields();
      }
    }, 4000);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [estimateId, form, onFinish, pineLabTransactionId, razorpayPosTransactionId]);

  const onSubmit = async (values: any) => {
    // First add the charge. The OMS caller supplies its own write (SET semantics + order
    // refresh); the default is the PI/estimate-level "add", behind its own permission.
    if (onAddCharge) {
      // Charge + payment travel in ONE request. The keys mirror what the estimate-payment API
      // takes; blanks are stripped so the backend's "required when" rules see absence, not "".
      // payment_amount may be omitted — it defaults to the charge amount server-side.
      const payment = values?.payment_type
        ? Object.fromEntries(
            Object.entries({
              payment_method: values.payment_type,
              // Razorpay / POS-Manual carry the operator-picked mode (UPI/Card/…) — the backend
              // resolver REQUIRES it for those two; the POS-device flows carry their own sub-mode.
              payment_mode:
                values.payment_mode ??
                values.pos_payment_mode ??
                values.razorpay_pos_payment_mode,
              payment_medium: values.payment_medium,
              payment_amount: values.payment_amount,
              organisation_branch_id: values.organisation_branch_id,
              payment_reference: values.payment_reference_id,
              collected_by_id: values.collector,
              pan_number: values.pan_number,
              // Extra context rides into payment_response verbatim.
              ezetap_device: values.ezetap_device,
            }).filter(([, v]) => v !== undefined && v !== null && v !== "")
          )
        : undefined;
      // Express has no charge field — the Payment Amount doubles as the charge, so send it as the
      // charge amount (undefined when no payment is collected → upgrade without charging).
      const effectiveCharge =
        chargeType === "express_delivery" ? values?.payment_amount : chargeAmount;
      setSubmitting(true);
      const ok = await onAddCharge({
        amount: effectiveCharge || undefined,
        reason: values?.reason,
        payment,
      });
      setSubmitting(false);
      if (!ok) return; // refused — the API toasted its reason; leave the form up
      onFinish({ ...values, charge_amount: effectiveCharge });
      return;
    } else {
      if (!canEdit) {
        message.error("You don't have permission to add this charge");
        return;
      }
      try {
        if (chargeType === "express_delivery") {
          await addExpressDeliveryCharge({
            estimate_id: estimateId,
            express_charge: chargeAmount,
            operation_type: "add",
          });
        } else if (chargeType === "unloading") {
          await addUnloadingCharge({
            estimate_id: estimateId,
            unloading_charge: chargeAmount,
            operation_type: "add",
          });
        }
        message.success(`${title} added successfully`);
      } catch (error) {
        message.error(`Failed to add ${title.toLowerCase()}`);
        return;
      }
    }

    // If no payment is selected, just finish
    if (!paymentType) {
      onFinish({ charge_amount: chargeAmount });
      return;
    }

    // Handle POS payment (Pinelabs)
    if (values?.payment_type === "pos") {
      const payload = {
        estimate_id: estimateId,
        payment_method: values?.pos_payment_mode,
        amount: values?.payment_amount,
      };

      if (!payload?.payment_method) {
        message.error("Please select a payment method");
        return;
      }
      if (!payload?.amount) {
        message.error("Please enter a payment amount");
        return;
      }
      if (
        !["upi", "card", "bank_emi", "brand_emi"].includes(
          payload?.payment_method
        )
      ) {
        message.error("Please select a valid payment method");
        return;
      }

      try {
        const transaction = await createPinelabTransaction({
          ...payload,
        });
        setPineLabTransactionId(transaction?.id);
        setLoading(true);
        message.success("Pinelab transaction created successfully");
      } catch (error) {
        console.error("Error creating Pinelab transaction:", error);
        message.error("Failed to create Pinelab transaction");
        return;
      }

      return;
    }

    // Handle Razorpay POS (Ezetap)
    if (values?.payment_type === "razorpay-pos") {
      const payload: any = {
        estimate_id: estimateId,
        payment_method: values?.razorpay_pos_payment_mode,
        amount: String(values?.payment_amount),
        device_id: values?.ezetap_device,
        branch_id: values?.organisation_branch_id,
      };

      if (!payload?.payment_method) {
        message.error("Please select a payment method");
        return;
      }
      if (!payload?.amount || payload.amount === "undefined") {
        message.error("Please enter a payment amount");
        return;
      }
      if (ezetapDevices?.length && !payload?.device_id) {
        message.error("Please select a Razorpay POS device");
        return;
      }

      try {
        const transaction = await createRazorpayPosTransaction(payload);
        if (!transaction) return;
        setRazorpayPosTransactionId(transaction?.id);
        setLoading(true);
        message.success("Razorpay POS transaction created successfully");
      } catch (error) {
        console.error("Error creating Razorpay POS transaction:", error);
        message.error("Failed to create Razorpay POS transaction");
        return;
      }

      return;
    }

    setSubmitting(true);

    let reference = values?.payment_reference_id;
    if (["cash", "waive-off"].includes(values?.payment_type)) {
      reference = values?.collector;
    }

    if (currentAmount > 2_00_000 && values?.payment_type === "cash") {
      if (!values?.pan_number) {
        message.error("Please enter the PAN number");
        setSubmitting(false);
        return;
      }
      await editUser(
        {
          pan: values?.pan_number,
        },
        estimateDetails?.client ?? estimateDetails?.client?.id
      );
    }

    const transaction = await createEstimateTransaction({
      estimate_id: estimateId,
      payment_method: values?.payment_type,
      payment_mode: values?.payment_mode,
      payment_medium: values?.payment_medium,
      payment_amount: values?.payment_amount,
      payment_reference: reference,
      organisation_branch_id: values?.organisation_branch_id,
    });

    form.resetFields();
    setTransactions((prev) => [...prev, transaction]);
    setPaymentType(undefined);
    setPosPaymentMode(undefined);

    setSubmitting(false);
    setLoading(false);
    onFinish({ ...values, charge_amount: chargeAmount });
  };

  const totalPaid = useMemo(() => {
    return (
      transactions
        ?.filter((transaction) =>
          ["captured", "authorized"].includes(transaction?.status)
        )
        .reduce((acc, transaction) => {
          return acc + Number(transaction.payment_amount ?? 0);
        }, 0) / 100
    );
  }, [transactions]);

  useEffect(() => {
    checkAndConfirmOrderPayment(estimateId as any, false).then((res) => {
      if (res) {
        message.success(
          "A payment has already been received for this order, please verify the pending amount."
        );
      }

      getEstimateTransactions(estimateId)
        .then((response) => {
          setTransactions(response);
        })
        .catch((error) => {
          console.error(error);
        });
    });

    fetchPIfromId(String(estimateId))
      .then((response) => {
        setEstimateDetails(response);
      })
      .catch((error) => {
        console.error(error);
      });

    fetchAllBranches()
      .then((res) => {
        setBranchList(res);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [estimateId]);

  useEffect(() => {
    if (!selectedBranch) {
      fetchBranchMembers()
        .then((res) => {
          setBranchMembers(res);
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      fetchBranchMembers({
        branch: selectedBranch,
      })
        .then((res) => {
          setBranchMembers(res);
        })
        .catch((error) => {
          console.error(error);
        });
      if (paymentType === "razorpay-pos") {
        ezetapDevicesList(selectedBranch);
      }
    }
  }, [selectedBranch]);

  const ezetapDevicesList = async (branch_id: number) => {
    setEzetapDevices(null);
    const res = await getEzetapBranchDetails(branch_id);
    if (res) {
      setEzetapDevices(res);
    }
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.payment_type) {
      const isWaiveOff = changedValues.payment_type === "waive-off";
      form.setFieldsValue({
        payment_reference_id: "",
        payment_mode: undefined,
        payment_medium: undefined,
        razorpay_pos_payment_mode: undefined,
        ezetap_device: undefined,
        // Waive-off collects nothing — freeze the amount at 0; clear it otherwise.
        payment_amount: isWaiveOff ? 0 : undefined,
      });
      setCurrentAmount(0);
      setPaymentType(changedValues.payment_type);
      if (changedValues.payment_type === "razorpay-pos" && selectedBranch) {
        ezetapDevicesList(selectedBranch);
      }
    }
  };

  if (loading) {
    return (
      <div>
        <div className="d-flex align-items-center justify-content-center gap-2">
          <div>Processing payment</div>
          <Spin />
        </div>
        <Button type="default" onClick={onPosCancel} className="mt-3">
          Cancel Payment
        </Button>
      </div>
    );
  }

  return (
    <Form
      layout="vertical"
      onFinish={onSubmit}
      onValuesChange={onValuesChange}
      form={form}
    >
      {description && (
        <p className="text-muted" style={{ marginTop: 0 }}>
          {description}
        </p>
      )}
      {/* Express has no separate charge field — the amount collected IS the charge, so the
          Payment Amount below doubles as the express charge (see onSubmit). Unloading keeps its
          own charge field since it can be set without a payment. */}
      {chargeType !== "express_delivery" && (
        <Form.Item
          label={`${title} Amount`}
          name="charge_amount"
          required={chargeRequired}
          rules={[
            ...(chargeRequired
              ? [{ required: true, message: `Please enter ${title.toLowerCase()} amount` }]
              : []),
            { type: 'number' as const, min: 0, message: 'Amount must be positive' }
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder={
              chargeRequired
                ? `Enter ${title.toLowerCase()} amount`
                : `Enter ${title.toLowerCase()} amount (optional)`
            }
            min={0}
            step={0.01}
            onChange={(value) => setChargeAmount(value || 0)}
          />
        </Form.Item>
      )}

      {showReason && (
        <Form.Item name="reason" label="Reason">
          <Input.TextArea rows={2} placeholder="Optional note for the escalation ledger" />
        </Form.Item>
      )}

      <div className="mb-3">
        <h6>Payment Details (Optional)</h6>
        <p className="text-muted">Add payment details if payment is being collected for this charge</p>
      </div>

      <Form.Item name="payment_type" label="Payment Type">
        <Select
          placeholder="Select Payment Type (Optional)"
          options={PaymentTypes(chargeAmount)}
          allowClear
        />
      </Form.Item>

      {["razorpay", "pos-manual"].includes(paymentType as string) && (
        <Form.Item
          name="payment_mode"
          label="Payment Mode"
          rules={[{ required: true, message: "Please select a payment mode" }]}
        >
          <Select placeholder="Select Payment Mode" options={PaymentModeOptions} />
        </Form.Item>
      )}
      {paymentType === "pos-manual" && (
        <Form.Item
          name="payment_medium"
          label="Payment Medium"
          rules={[{ required: true, message: "Please select a payment medium" }]}
        >
          <Select placeholder="Select Payment Medium" options={PaymentMediumOptions} />
        </Form.Item>
      )}

      {paymentType &&
        !["cash", "waive-off", "pos", "razorpay-pos"].includes(paymentType as string) && (
          <Form.Item
            name="payment_reference_id"
            label="Payment Reference ID"
            rules={[
              {
                required: true,
                message: "Please enter the payment reference ID",
              },
            ]}
          >
            <Input placeholder="Enter Payment Reference ID" />
          </Form.Item>
        )}
      {paymentType === "pos" && (
        <div>
          <Form.Item
            name="pos_payment_mode"
            label="POS Payment Mode"
            rules={[
              {
                required: true,
                message: "Please select a payment mode",
              },
            ]}
          >
            <Select
              placeholder="Select Payment Mode"
              options={POSPaymentModes}
              onChange={(value) => {
                setPosPaymentMode(value);
                form.setFieldsValue({
                  payment_reference_id: "",
                });
              }}
            />
          </Form.Item>
        </div>
      )}
      {paymentType === "razorpay-pos" && (
        <Form.Item
          name="razorpay_pos_payment_mode"
          label="Payment Mode"
          rules={[
            {
              required: true,
              message: "Please select a payment mode",
            },
          ]}
        >
          <Select
            placeholder="Select Payment Mode"
            options={RazorpayPOSPaymentModes}
          />
        </Form.Item>
      )}
      {paymentType && (
        <Form.Item name="organisation_branch_id" label="Branch" required>
          <Select
            placeholder="Select Branch"
            options={branchList?.map((branch) => ({
              label: branch?.branch_name,
              value: branch?.id,
            }))}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            className="w-100"
            onChange={(value) => {
              setSelectedBranch(value);
              form.setFieldsValue({ ezetap_device: undefined });
            }}
          />
        </Form.Item>
      )}
      {paymentType === "razorpay-pos" && selectedBranch && (
        <Form.Item
          name="ezetap_device"
          label="Razorpay POS Device"
          rules={[
            {
              required: true,
              message: "Please select a Razorpay POS device",
            },
          ]}
        >
          <Select
            placeholder="Select Razorpay POS Device"
            options={ezetapDevices?.map((device: any) => ({
              label: device?.device_name
                ? `${device.device_name} (${device.device_serial})`
                : device.device_serial,
              value: device?.id,
            }))}
          />
        </Form.Item>
      )}
      {["cash", "waive-off"].includes(paymentType as string) && (
        <Form.Item
          name="collector"
          label={paymentType === "cash" ? "Collected By" : "Waived Off By"}
          rules={[
            {
              required: true,
              message: "Please enter the name of the collector",
            },
          ]}
        >
          <Select
            placeholder="Select Collector"
            options={allbranchMembers
              ?.filter((member) => [1, 2].includes(member?.user_permission))
              ?.map((member) => ({
                value: member.user?.id,
                label: member.user?.f_name,
              }))}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            className="w-100"
          ></Select>
        </Form.Item>
      )}
      {paymentType && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="fw-bold">Amount Pending</div>
              <div>
                ₹{" "}
                {(
                  Number(estimateDetails?.total_estimate_amount) - Number(totalPaid)
                ).toFixed(2)}
              </div>
            </div>
            <div>
              <div className="fw-bold">Total Paid</div>
              <div>₹ {totalPaid}</div>
            </div>
          </div>
          <Form.Item
            label="Payment Amount"
            name="payment_amount"
            required
            rules={[
              {
                type: "regexp",
                pattern: /^[0-9]+$/,
              },
              {
                validator: async (_, value) => {
                  if (paymentType === "waive-off") {
                    if (Number(value) > 1000) {
                      return Promise.reject(
                        new Error("Amount cannot be more than ₹1000")
                      );
                    }
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              className="w-100"
              placeholder="Enter Payment Amount"
              min={0}
              // Waive-off collects nothing — the amount is locked at 0.
              disabled={paymentType === "waive-off"}
              onChange={(n) => {
                setCurrentAmount(n || 0);
              }}
            />
          </Form.Item>
          {currentAmount > 2_00_000 && paymentType === "cash" && (
            <Form.Item
              name="pan_number"
              label="PAN Number"
              rules={[
                {
                  required: true,
                  message: "Please enter the PAN number",
                },
                {
                  pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                  message: "Please enter a valid PAN number",
                },
              ]}
            >
              <Input placeholder="Enter PAN Number" />
            </Form.Item>
          )}
        </>
      )}
      <Form.Item className="d-flex justify-content-end">
        <Space>
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>
          {/* With a caller-supplied charge write the caller's menu owns gating — the PI
              permission must not lock an OMS admin out. */}
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={!onAddCharge && !canEdit}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}